import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const BASE_API = process.env.REACT_APP_API_URL || "https://wa-api.globebot.io";
const UPLOAD_API = `${BASE_API}/auth/file/upload/path`; // assumes backend endpoint as per @authRoutesForSingleTable.ts

const CAT_LABELS = {
  AUTO: "Automotive", BEAUTY: "Beauty", APPAREL: "Apparel", EDU: "Education",
  ENTERTAIN: "Entertainment", ONLINE_GAMBLING: "Online Gambling",
  PHYSICAL_GAMBLING: "Physical Gambling", EVENT_PLAN: "Event Planning",
  FINANCE: "Finance", GROCERY: "Grocery", ALCOHOL: "Alcohol",
  GOVT: "Government", HOTEL: "Hotel & Hospitality", HEALTH: "Healthcare",
  OTC_DRUGS: "OTC Drugs", NONPROFIT: "Nonprofit / NGO",
  PROF_SERVICES: "Professional Services", RETAIL: "Retail",
  TRAVEL: "Travel", RESTAURANT: "Restaurant & Food", OTHER: "Other",
};

const CATEGORIES = Object.entries(CAT_LABELS);

// ── Token helper ──────────────────────────────────────────────────────────────
function decodeToken(token) {
  if (!token) return {};
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return {};
  }
}

function getAuthToken() {
  if (
    typeof window !== "undefined" &&
    typeof window.locatorgarsotrga === "function"
  ) {
    return window.locatorgarsotrga("token") || null;
  }
  return typeof window !== "undefined"
    ? localStorage.getItem("token")
    : null;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Syne:wght@700;800&display=swap');

  .bp-root {
    --green: #00816a;
    --green-light: #e6f4f1;
    --green-mid: #00a688;
    --green-dark: #005f4e;
    --accent: #25d366;
    --bg: #f0f4f7;
    --card: #ffffff;
    --border: #e2e8f0;
    --text: #1a202c;
    --muted: #64748b;
    --danger: #ef4444;
    --warning: #f59e0b;
    --radius: 14px;
    --shadow: 0 2px 16px rgba(0,0,0,0.07);
    font-family: 'DM Sans', sans-serif;
    color: var(--text);
    background: var(--bg);
    min-height: 100vh;
  }

  /* ── Topbar ── */
  .bp-topbar {
    background: linear-gradient(135deg, var(--green-dark) 0%, var(--green) 60%, var(--green-mid) 100%);
    padding: 0 2rem;
    height: 60px;
    display: flex;
    align-items: center;
    gap: 12px;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 12px rgba(0,129,106,.35);
  }
  .bp-topbar h1 {
    font-family: 'Syne', sans-serif;
    font-size: 1.1rem;
    font-weight: 800;
    color: #fff;
    margin: 0;
    letter-spacing: .2px;
  }
  .bp-topbar-icon { color: rgba(255,255,255,.9); font-size: 1.4rem; }
  .bp-topbar-right { margin-left: auto; display: flex; align-items: center; gap: 10px; }
  .bp-topbar-badge {
    background: rgba(255,255,255,.18);
    color: #fff;
    font-size: .72rem;
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
    border: 1px solid rgba(255,255,255,.25);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .bp-quality-badge {
    font-size: .68rem;
    padding: 3px 10px;
    border-radius: 20px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .5px;
  }
  .bp-quality-badge.GREEN  { background: rgba(37,211,102,.25); color: #25d366; border: 1px solid rgba(37,211,102,.4); }
  .bp-quality-badge.YELLOW { background: rgba(245,158,11,.25);  color: #f59e0b; border: 1px solid rgba(245,158,11,.4); }
  .bp-quality-badge.RED    { background: rgba(239,68,68,.25);   color: #ef4444; border: 1px solid rgba(239,68,68,.4); }

  /* ── WABA Info strip ── */
  .bp-waba-strip {
    background: var(--card);
    border-bottom: 1px solid var(--border);
    padding: .6rem 2rem;
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .bp-waba-item { display: flex; flex-direction: column; gap: 1px; }
  .bp-waba-label { font-size: .65rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; }
  .bp-waba-value { font-size: .8rem; font-weight: 600; color: var(--text); font-family: 'DM Sans', monospace; }

  /* ── Layout ── */
  .bp-layout {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
    padding: 1.5rem;
  }

  /* ── Card ── */
  .bp-card {
    background: var(--card);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    padding: 1.75rem;
    margin-bottom: 1.25rem;
    border: 1px solid var(--border);
    transition: box-shadow .2s;
  }
  .bp-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,.1); }
  .bp-card-header {
    display: flex; align-items: flex-start; gap: 10px;
    margin-bottom: 1.4rem; padding-bottom: 1rem;
    border-bottom: 1px solid var(--border);
  }
  .bp-card-header-icon {
    width: 38px; height: 38px;
    background: var(--green-light);
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--green); font-size: 1rem; flex-shrink: 0;
  }
  .bp-card-header-text h2 {
    font-family: 'Syne', sans-serif;
    font-size: 1rem; font-weight: 700; color: var(--text); margin: 0;
  }
  .bp-card-header-text p { font-size: .8rem; color: var(--muted); margin: 2px 0 0; }

  /* ── Avatar ── */
  .bp-avatar-row { display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; }
  .bp-avatar-wrap { position: relative; flex-shrink: 0; }
  .bp-avatar-circle {
    width: 90px; height: 90px; border-radius: 50%;
    background: var(--green-light);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; border: 2.5px dashed var(--green);
    cursor: pointer; transition: border-color .2s, transform .2s;
  }
  .bp-avatar-circle:hover { border-color: var(--green-mid); transform: scale(1.03); }
  .bp-avatar-circle img { width: 100%; height: 100%; object-fit: cover; }
  .bp-avatar-circle i { color: var(--green); font-size: 2rem; }
  .bp-avatar-badge {
    position: absolute; bottom: 0; right: 0;
    width: 26px; height: 26px; border-radius: 50%;
    background: var(--green); color: #fff;
    font-size: .65rem; display: flex; align-items: center; justify-content: center;
    border: 2px solid #fff; cursor: pointer;
    transition: background .2s;
  }
  .bp-avatar-badge:hover { background: var(--green-mid); }
  .bp-upload-box {
    flex: 1; border: 2px dashed var(--border); border-radius: 10px;
    padding: 1rem 1.25rem; text-align: center; cursor: pointer;
    transition: border-color .2s, background .2s; background: var(--bg);
  }
  .bp-upload-box:hover, .bp-upload-box.drag-over {
    border-color: var(--green); background: var(--green-light);
  }
  .bp-upload-box i { color: var(--muted); font-size: 1.6rem; display: block; margin-bottom: .4rem; }
  .bp-upload-title { font-weight: 600; font-size: .85rem; color: var(--text); }
  .bp-upload-sub { font-size: .73rem; color: var(--muted); margin-top: 3px; }

  /* ── Form ── */
  .bp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
  .bp-form-group { display: flex; flex-direction: column; gap: 5px; }
  .bp-label {
    font-size: .8rem; font-weight: 600; color: var(--text);
    display: flex; align-items: center; gap: 6px; margin: 0;
  }
  .bp-label i { color: var(--green); font-size: .75rem; }
  .bp-input, .bp-select, .bp-textarea {
    font-family: 'DM Sans', sans-serif;
    font-size: .875rem; color: var(--text);
    background: var(--bg); border: 1.5px solid var(--border);
    border-radius: 8px; padding: 9px 12px; outline: none;
    transition: border-color .2s, box-shadow .2s, background .2s;
    width: 100%; box-sizing: border-box;
  }
  .bp-input:focus, .bp-select:focus, .bp-textarea:focus {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(0,129,106,.1);
    background: #fff;
  }
  .bp-input[readonly], .bp-input[readonly]:focus {
    background: #f1f5f9 !important;
    color: var(--muted); cursor: not-allowed;
    border-color: var(--border); box-shadow: none;
  }
  .bp-textarea { resize: vertical; min-height: 90px; }
  .bp-hint { font-size: .72rem; color: var(--muted); }
  .bp-websites-row { display: flex; gap: .75rem; }
  .bp-websites-row .bp-input { flex: 1; }

  /* ── Char counter ── */
  .bp-char-counter { font-size: .7rem; color: var(--muted); text-align: right; margin-top: 2px; }
  .bp-char-counter.warn { color: var(--warning); }
  .bp-char-counter.over { color: var(--danger); }

  /* ── Buttons ── */
  .bp-btn-row { display: flex; justify-content: flex-end; gap: .75rem; margin-top: 1.25rem; }
  .bp-btn {
    font-family: 'DM Sans', sans-serif;
    font-size: .85rem; font-weight: 600;
    padding: 9px 22px; border-radius: 8px; border: none;
    cursor: pointer;
    display: inline-flex; align-items: center; gap: 7px;
    transition: all .2s;
  }
  .bp-btn-outline {
    background: #fff; border: 1.5px solid var(--border); color: var(--muted);
  }
  .bp-btn-outline:hover { border-color: var(--text); color: var(--text); }
  .bp-btn-primary { background: var(--green); color: #fff; }
  .bp-btn-primary:hover { background: var(--green-mid); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,129,106,.3); }
  .bp-btn-primary:active { transform: translateY(0); }
  .bp-btn-primary:disabled { background: var(--border); cursor: not-allowed; transform: none; box-shadow: none; }

  /* ── Toast ── */
  .bp-toast {
    position: fixed; bottom: 1.5rem; right: 1.5rem; z-index: 9999;
    background: #1a202c; color: #fff;
    padding: 12px 20px; border-radius: 10px;
    font-size: .85rem; font-weight: 500;
    display: flex; align-items: center; gap: 9px;
    box-shadow: 0 4px 24px rgba(0,0,0,.25);
    opacity: 0; transform: translateY(14px);
    transition: all .3s cubic-bezier(.34,1.56,.64,1);
    pointer-events: none;
  }
  .bp-toast.show { opacity: 1; transform: translateY(0); }
  .bp-toast.success { background: var(--green); }
  .bp-toast.error   { background: var(--danger); }

  /* ── Spinner ── */
  .bp-spinner {
    width: 16px; height: 16px;
    border: 2.5px solid rgba(255,255,255,.4);
    border-top-color: #fff; border-radius: 50%;
    animation: bp-spin .7s linear infinite; display: inline-block;
  }
  @keyframes bp-spin { to { transform: rotate(360deg); } }

  /* ── Skeleton ── */
  .bp-skeleton {
    background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
    background-size: 200% 100%;
    animation: bp-shimmer 1.4s infinite;
    border-radius: 6px; height: 14px;
  }
  @keyframes bp-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  /* ── Phone ── */
  .bp-phone-sticky { position: sticky; top: 74px; }
  .bp-phone-label {
    text-align: center; font-size: .75rem; color: var(--muted);
    font-weight: 700; margin-bottom: .75rem;
    letter-spacing: 1px; text-transform: uppercase;
  }
  .bp-phone-shell {
    background: #0d0d0d;
    border-radius: 46px; padding: 12px;
    box-shadow: 0 24px 64px rgba(0,0,0,.4),
                inset 0 0 0 1px rgba(255,255,255,.07),
                inset 0 1px 0 rgba(255,255,255,.12);
    width: 280px; margin: 0 auto;
  }
  .bp-phone-screen { background: #e5ddd5; border-radius: 36px; overflow: hidden; }
  .bp-phone-notch {
    background: #0d0d0d; height: 28px; width: 88px;
    border-radius: 0 0 16px 16px; margin: 0 auto;
    position: relative; z-index: 2;
  }
  .bp-phone-statusbar {
    background: var(--green);
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 12px; font-size: .62rem; color: #fff;
    margin-top: -28px; padding-top: 32px;
  }
  .bp-phone-chatbar {
    background: var(--green);
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px;
  }
  .bp-phone-avatar-sm {
    width: 34px; height: 34px; border-radius: 50%;
    background: #fff; display: flex; align-items: center; justify-content: center;
    font-size: .8rem; color: var(--green); overflow: hidden; flex-shrink: 0;
  }
  .bp-phone-avatar-sm img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
  .bp-phone-chatbar-name { font-size: .75rem; font-weight: 700; color: #fff; }
  .bp-phone-chatbar-sub { font-size: .6rem; color: rgba(255,255,255,.8); }
  .bp-phone-msgs {
    padding: 10px; min-height: 340px; max-height: 340px; overflow-y: auto;
    background-color: #ddd;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b0b0b0' fill-opacity='0.12'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
  .bp-msg-bubble {
    background: #fff; border-radius: 0 10px 10px 10px;
    padding: 10px; font-size: .75rem; color: #111;
    box-shadow: 0 1px 3px rgba(0,0,0,.12); margin-bottom: 8px;
  }
  .bp-biz-avatar {
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--green-light);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 8px; color: var(--green);
    font-size: 1.4rem; overflow: hidden;
    border: 2px dashed var(--green);
  }
  .bp-biz-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
  .bp-biz-name { text-align: center; font-weight: 700; font-size: .8rem; margin-bottom: 8px; color: #111; }
  .bp-share-btn {
    display: flex; align-items: center; justify-content: center; gap: 5px;
    background: #f4f6f8; border-radius: 6px; padding: 5px 8px;
    font-size: .7rem; color: var(--muted); border: 1px solid #e2e8f0;
  }
  .bp-msg-info { border-radius: 0 10px 10px 10px; }
  .bp-msg-info p {
    display: flex; align-items: flex-start; gap: 6px;
    margin-bottom: 4px; font-size: .72rem;
  }
  .bp-msg-info p:last-child { margin-bottom: 0; }
  .bp-msg-info i { color: var(--green); font-size: .68rem; margin-top: 2px; flex-shrink: 0; }
  .bp-phone-inputbar {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 8px; background: #f0f0f0;
  }
  .bp-phone-input-fake {
    flex: 1; background: #fff; border-radius: 20px;
    padding: 6px 12px; font-size: .68rem; color: var(--muted);
    display: flex; align-items: center; justify-content: space-between;
  }
  .bp-phone-mic {
    width: 34px; height: 34px; border-radius: 50%;
    background: var(--green); color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: .75rem;
  }

  /* ── Loading overlay ── */
  .bp-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 300px; gap: 12px; color: var(--muted);
  }
  .bp-loading-spinner {
    width: 36px; height: 36px;
    border: 3px solid var(--green-light);
    border-top-color: var(--green);
    border-radius: 50%;
    animation: bp-spin .8s linear infinite;
  }

  @media(max-width: 900px) {
    .bp-layout { grid-template-columns: 1fr; }
    .bp-phone-sticky { position: static; }
    .bp-form-grid { grid-template-columns: 1fr; }
    .bp-waba-strip { padding: .6rem 1rem; gap: 1rem; }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────
const BusinessProfile = ({ wabaData }) => {
  const phone_number_id =
    new URLSearchParams(window.location.search).get("id") || "";

  // Get local copy if not present
  let localWabaData = null;
  try {
    const localDataStr = localStorage.getItem("wabaData");
    if (localDataStr) {
      localWabaData = JSON.parse(localDataStr);
    }
  } catch (e) {
    localWabaData = null;
  }

  const dataSource =
    wabaData && Object.keys(wabaData).length > 0
      ? wabaData
      : localWabaData || {};

  const displayName = dataSource?.["Display Name"] || "";
  const wabaNumber = dataSource?.["WABA Number"] || "";
  const wabaId =
    dataSource?.waba_id || dataSource?.["WABA BusinessId"] || "";
  const qualityRating = dataSource?.["Quality Rating"] || "";

  const token = getAuthToken();

  const [avatarSrc, setAvatarSrc] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    msg: "",
    type: "success",
  });

  const [form, setForm] = useState({
    category: "OTHER",
    description: "",
    email: "",
    address: "",
    website: "",
    website2: "",
  });

  // Track meta loaded from API: { meta_key: meta_value }
  const [metaLoaded, setMetaLoaded] = useState({});
  // Track metaId for each (for update endpoint)
  const [metaIdMap, setMetaIdMap] = useState({});

  const fileRef = useRef(null);
  const fileObj = useRef(null);

  const authHeaders = token ? { Authorization: `${token}` } : {};

  // ── Toast ─────
  const showToast = useCallback((msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3200);
  }, []);

  // ── Load whatsapp_account_meta ─────
  const loadMeta = useCallback(async () => {
    if (!phone_number_id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_API}/auth/whatsapp-meta/${encodeURIComponent(
          phone_number_id
        )}`,
        { headers: authHeaders }
      );
      const metaRows = res.data?.meta || [];
      const map = {};
      const idMap = {};
      metaRows.forEach((m) => {
        map[m.meta_key] = m.meta_value;
        idMap[m.meta_key] = m.id;
      });

      setForm((f) => ({
        ...f,
        category:
          typeof map.category !== "undefined"
            ? map.category
            : "OTHER",
        description:
          typeof map.description !== "undefined"
            ? map.description
            : "",
        email:
          typeof map.email !== "undefined"
            ? map.email
            : "",
        address:
          typeof map.address !== "undefined"
            ? map.address
            : "",
        website:
          typeof map.website !== "undefined"
            ? map.website
            : "",
        website2:
          typeof map.website2 !== "undefined"
            ? map.website2
            : "",
      }));
      setMetaLoaded({
        category:
          typeof map.category !== "undefined" &&
          map.category !== null
            ? map.category
            : "",
        description:
          typeof map.description !== "undefined" &&
          map.description !== null
            ? map.description
            : "",
        email:
          typeof map.email !== "undefined" && map.email !== null
            ? map.email
            : "",
        address:
          typeof map.address !== "undefined" &&
          map.address !== null
            ? map.address
            : "",
        website:
          typeof map.website !== "undefined" &&
          map.website !== null
            ? map.website
            : "",
        website2:
          typeof map.website2 !== "undefined" &&
          map.website2 !== null
            ? map.website2
            : "",
      });
      setMetaIdMap(idMap);
      // Changed: prefer 'biz_profile_picture' or fallback to profile_picture
      if (map.biz_profile_picture) setAvatarSrc(`${BASE_API}/${map.biz_profile_picture}`);
      else if (map.profile_picture) setAvatarSrc(`${BASE_API}/${map.profile_picture}`);
    } catch (err) {
      console.warn("Meta load error:", err);
    } finally {
      setLoading(false);
    }
  }, [phone_number_id]);

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line
  }, [loadMeta]);

  // ── File handling ─────
  const handleFile = (file) => {
    if (!file) return;
    fileObj.current = file;
    const reader = new FileReader();
    reader.onload = (e) => setAvatarSrc(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  // If the value is just whitespace, consider as empty
  function cleanMetaValue(val) {
    if (typeof val === "undefined" || val === null) return "";
    if (typeof val === "string" && val.trim() === "") return "";
    return val;
  }

  // ── Save ─────
  const saveProfile = async () => {
    if (!phone_number_id) {
      showToast("No PhoneNumber ID found.", "error");
      return;
    }
    setSaving(true);
    try {
      // Prepare changes: include only if value has changed OR was erased by user
      const candidateFields = [
        { meta_key: "category", meta_value: cleanMetaValue(form.category) },
        {
          meta_key: "description",
          meta_value: cleanMetaValue(form.description),
        },
        { meta_key: "email", meta_value: cleanMetaValue(form.email) },
        { meta_key: "address", meta_value: cleanMetaValue(form.address) },
        { meta_key: "website", meta_value: cleanMetaValue(form.website) },
        {
          meta_key: "website2",
          meta_value: cleanMetaValue(form.website2),
        },
      ];

      // Only submit changed vals, or erased vals
      const changesToSend = [];
      for (const f of candidateFields) {
        if (
          (metaLoaded[f.meta_key] !== undefined &&
            metaLoaded[f.meta_key] !== f.meta_value) ||
          (metaLoaded[f.meta_key] === undefined &&
            f.meta_value !== "" &&
            f.meta_value !== null)
        ) {
          changesToSend.push({
            meta_key: f.meta_key,
            meta_value: f.meta_value,
          });
        }
      }

      let avatarPathToSend = null;

      // Media upload part: First upload file (if present) to media upload endpoint. Only send meta with the path.
      if (fileObj.current) {
        const formData = new FormData();
        formData.append("file", fileObj.current);
        // optional business logic: add folder or type, if required
        // formData.append("table", "whatsapp_profile"); // for backend side grouping
      let foldername = displayName.split(" ")[0];
      console.log("folder ",foldername);
      alert("checking..")
;        const uploadRes = await axios.post(`${UPLOAD_API}?name=${foldername || ''}&wabaId=${wabaId}`, formData, {
          headers: {
            ...authHeaders,
            // let browser set multipart boundaries
          },
        });
   
        if (
          uploadRes.data &&
          uploadRes.data.success &&
          uploadRes.data.filePath // or url?
        ) {
          avatarPathToSend = uploadRes.data.filePath;
          // Now: append/update meta for biz_profile_picture (or profile_picture as needed)
          changesToSend.push({
            meta_key: "biz_profile_picture",
            meta_value: avatarPathToSend,
          });
        } else {
          throw new Error("Image upload failed.");
        }
      }

      // No change? no-op unless new picture
      if (changesToSend.length === 0) {
        showToast("No changes to save.");
        setSaving(false);
        return;
      }

      // For each meta to write, check if an id exists (use PUT if exists, otherwise POST)
      await Promise.all(
        changesToSend.map(async ({ meta_key, meta_value }) => {
          const metaId = metaIdMap[meta_key];
          if (metaId) {
            // Exists — update
            await axios.put(
              `${BASE_API}/auth/whatsapp-meta/${phone_number_id}/${meta_key}`,
              { phoneNumberId: phone_number_id, meta_key, meta_value },
              {
                headers: {
                  ...authHeaders,
                  "Content-Type": "application/json",
                },
              }
            );
          } else {
            // Not exists — create
            await axios.post(
              `${BASE_API}/auth/whatsapp-meta`,
              { phoneNumberId: phone_number_id, meta_key, meta_value },
              {
                headers: {
                  ...authHeaders,
                  "Content-Type": "application/json",
                },
              }
            );
          }
        })
      );

      // After save, reload meta for updated state
      await loadMeta();
      showToast("Business profile saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Failed to save. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (e) => {
    setForm((f) => ({
      ...f,
      [key]: typeof e.target.value === "string" ? e.target.value : "",
    }));
  };

  // ── Render ─────
  return (
    <div className="bp-root">
      <style>{css}</style>

      {/* ── Topbar ── */}
      <div className="bp-topbar !hidden">
        <i className="fa-brands fa-whatsapp bp-topbar-icon" />
        <h1>Business Profile</h1>
        <div className="bp-topbar-right">
          {qualityRating && (
            <span className={`bp-quality-badge ${qualityRating}`}>
              <i
                className="fas fa-circle"
                style={{ fontSize: ".45rem", marginRight: 4 }}
              />
              {qualityRating}
            </span>
          )}
          <span className="bp-topbar-badge">
            {displayName || wabaNumber}
          </span>
        </div>
      </div>

      {/* ── WABA Info Strip ── */}
      {(wabaNumber || wabaId || phone_number_id) && (
        <div className="bp-waba-strip !hidden">
          {wabaNumber && (
            <div className="bp-waba-item">
              <span className="bp-waba-label">WABA Number</span>
              <span className="bp-waba-value">{wabaNumber}</span>
            </div>
          )}
          {phone_number_id && (
            <div className="bp-waba-item">
              <span className="bp-waba-label">Phone Number ID</span>
              <span className="bp-waba-value">{phone_number_id}</span>
            </div>
          )}
          {wabaId && (
            <div className="bp-waba-item">
              <span className="bp-waba-label">WABA Business ID</span>
              <span className="bp-waba-value">{wabaId}</span>
            </div>
          )}
          {wabaData?.ConfigureDate && (
            <div className="bp-waba-item">
              <span className="bp-waba-label">Configured</span>
              <span className="bp-waba-value">
                {new Date(wabaData.ConfigureDate).toLocaleDateString(
                  "en-IN",
                  {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }
                )}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="bp-layout">
        {/* ── LEFT COLUMN ── */}
        <div>
          {loading ? (
            <div className="bp-card">
              <div className="bp-loading">
                <div className="bp-loading-spinner" />
                <span>Loading profile data…</span>
              </div>
            </div>
          ) : (
            <>
              {/* Personal Info Card */}
              <div className="bp-card">
                <div className="bp-card-header">
                  <div className="bp-card-header-icon">
                    <i className="fas fa-store" />
                  </div>
                  <div className="bp-card-header-text">
                    <h2>Personal Info</h2>
                    <p>
                      Update your WhatsApp Business profile photo and
                      details
                    </p>
                  </div>
                </div>

                {/* Avatar */}
                <div className="bp-avatar-row">
                  <div className="bp-avatar-wrap">
                    <div
                      className="bp-avatar-circle"
                      onClick={() => fileRef.current?.click()}
                    >
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="avatar" />
                      ) : (
                        <i className="fas fa-store" />
                      )}
                    </div>
                    <div
                      className="bp-avatar-badge"
                      onClick={() => fileRef.current?.click()}
                    >
                      <i className="fas fa-camera" />
                    </div>
                  </div>

                  <div
                    className={`bp-upload-box${dragOver ? " drag-over" : ""}`}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <i className="fas fa-cloud-upload-alt" />
                    <div className="bp-upload-title">
                      Click to upload profile picture
                    </div>
                    <div className="bp-upload-sub">
                      PNG or JPG · max 10 MB · recommended 400×400px
                    </div>
                  </div>

                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleFile(e.target.files[0])}
                  />
                </div>

                {/* Display Name (read-only from WABA) + Category */}
                <div className="bp-form-grid">
                  <div className="bp-form-group">
                    <label className="bp-label">
                      <i className="fas fa-user-tag" /> Display Name
                    </label>
                    <input className="bp-input" value={displayName} readOnly />
                    <span className="bp-hint">
                      Managed by WhatsApp Business — cannot be edited here
                    </span>
                  </div>
                  <div className="bp-form-group">
                    <label className="bp-label">
                      <i className="fas fa-tags" /> Category
                    </label>
                    <select
                      className="bp-select"
                      value={form.category}
                      onChange={setField("category")}
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      {CATEGORIES.map(([val, label]) => (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div className="bp-form-group">
                  <label className="bp-label">
                    <i className="fas fa-align-left" /> About / Description
                  </label>
                  <textarea
                    className="bp-textarea"
                    placeholder="Describe your business…"
                    value={form.description}
                    onChange={setField("description")}
                    maxLength={256}
                  />
                  <div
                    className={`bp-char-counter${
                      form.description.length > 230
                        ? form.description.length > 256
                          ? " over"
                          : " warn"
                        : ""
                    }`}
                  >
                    {form.description.length}/256
                  </div>
                </div>
              </div>

              {/* Contact Info Card */}
              <div className="bp-card">
                <div className="bp-card-header">
                  <div className="bp-card-header-icon">
                    <i className="fas fa-address-book" />
                  </div>
                  <div className="bp-card-header-text">
                    <h2>Contact Info</h2>
                    <p>Manage address, email, and website details</p>
                  </div>
                </div>

                <div className="bp-form-grid">
                  <div className="bp-form-group">
                    <label className="bp-label">
                      <i className="fas fa-envelope" /> Business Email
                    </label>
                    <input
                      className="bp-input"
                      type="email"
                      placeholder="contact@yourbusiness.com"
                      value={form.email}
                      onChange={setField("email")}
                    />
                  </div>
                  <div className="bp-form-group">
                    <label className="bp-label">
                      <i className="fas fa-phone" /> WABA Number
                    </label>
                    <input
                      className="bp-input"
                      type="tel"
                      value={wabaNumber}
                      readOnly
                    />
                  </div>
                </div>

                <div
                  className="bp-form-group"
                  style={{ marginTop: ".85rem" }}
                >
                  <label className="bp-label">
                    <i className="fas fa-map-marker-alt" /> Address
                  </label>
                  <textarea
                    className="bp-textarea"
                    placeholder="Enter your business address"
                    rows={2}
                    value={form.address}
                    onChange={setField("address")}
                    maxLength={256}
                  />
                </div>

                <div
                  className="bp-form-group"
                  style={{ marginTop: ".85rem" }}
                >
                  <label className="bp-label">
                    <i className="fas fa-globe" /> Website(s)
                  </label>
                  <div className="bp-websites-row">
                    <input
                      className="bp-input"
                      type="url"
                      placeholder="https://www.yourwebsite.com"
                      value={form.website}
                      onChange={setField("website")}
                    />
                    <input
                      className="bp-input"
                      type="url"
                      placeholder="https://www.second-site.com"
                      value={form.website2}
                      onChange={setField("website2")}
                    />
                  </div>
                </div>

                <div className="bp-btn-row">
                  <button
                    className="bp-btn bp-btn-outline"
                    onClick={loadMeta}
                    disabled={saving}
                  >
                    <i className="fas fa-undo" /> Reset
                  </button>
                  <button
                    className="bp-btn bp-btn-primary"
                    onClick={saveProfile}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="bp-spinner" /> Saving…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save" /> Save Profile
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── RIGHT – Phone Preview ── */}
        <div className="bp-phone-sticky">
          <div className="bp-phone-label">Live Preview</div>
          <div className="bp-phone-shell">
            <div className="bp-phone-screen">
              <div className="bp-phone-notch" />

              {/* Status bar */}
              <div className="bp-phone-statusbar">
                <span>11:20</span>
                <div
                  style={{ display: "flex", gap: 5, alignItems: "center" }}
                >
                  <i
                    className="fas fa-wifi"
                    style={{ fontSize: ".58rem" }}
                  />
                  <i
                    className="fas fa-signal"
                    style={{ fontSize: ".58rem" }}
                  />
                  <i
                    className="fas fa-battery-three-quarters"
                    style={{ fontSize: ".58rem" }}
                  />
                </div>
              </div>

              {/* Chat bar */}
              <div className="bp-phone-chatbar">
                <i
                  className="fas fa-arrow-left"
                  style={{ color: "#fff", fontSize: ".7rem" }}
                />
                <div className="bp-phone-avatar-sm">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="preview" />
                  ) : (
                    <i
                      className="fas fa-store"
                      style={{ fontSize: ".8rem" }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="bp-phone-chatbar-name">
                    {displayName || "Business Name"}
                  </div>
                  <div className="bp-phone-chatbar-sub">
                    Business Account · {wabaNumber}
                  </div>
                </div>
                <i
                  className="fas fa-shop"
                  style={{ color: "#fff", fontSize: ".7rem" }}
                />
              </div>

              {/* Messages */}
              <div className="bp-phone-msgs">
                {/* Bubble 1 – avatar + share */}
                <div className="bp-msg-bubble">
                  <div className="bp-biz-avatar">
                    {avatarSrc ? (
                      <img src={avatarSrc} alt="biz" />
                    ) : (
                      <i className="fas fa-store" />
                    )}
                  </div>
                  <div className="bp-biz-name">
                    {displayName || "Business Name"}
                  </div>
                  <div className="bp-share-btn">
                    <i
                      className="fas fa-share"
                      style={{ fontSize: ".6rem" }}
                    />{" "}
                    Share
                  </div>
                </div>

                {/* Bubble 2 – info */}
                <div className="bp-msg-bubble bp-msg-info">
                  {form.description && (
                    <p>
                      <i className="fas fa-briefcase" />
                      <span>{form.description}</span>
                    </p>
                  )}
                  <p>
                    <i className="fas fa-shapes" />
                    <span>
                      {CAT_LABELS[form.category] || "Other"}
                    </span>
                  </p>
                  {form.address && (
                    <p>
                      <i className="fas fa-map-marker-alt" />
                      <span>{form.address}</span>
                    </p>
                  )}
                  {form.email && (
                    <p>
                      <i className="fas fa-envelope" />
                      <span>{form.email}</span>
                    </p>
                  )}
                  {form.website && (
                    <p>
                      <i className="fas fa-globe" />
                      <span style={{ color: "var(--green)" }}>
                        {form.website}
                      </span>
                    </p>
                  )}
                  {form.website2 && (
                    <p>
                      <i className="fas fa-globe" />
                      <span>{form.website2}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Input bar */}
              <div className="bp-phone-inputbar">
                <div className="bp-phone-input-fake">
                  <span>Enter your message</span>
                  <span>
                    <i
                      className="fas fa-paperclip"
                      style={{
                        marginRight: 6,
                        fontSize: ".68rem",
                        color: "var(--muted)",
                      }}
                    />
                    <i
                      className="fas fa-camera"
                      style={{
                        fontSize: ".68rem",
                        color: "var(--muted)",
                      }}
                    />
                  </span>
                </div>
                <div className="bp-phone-mic">
                  <i
                    className="fas fa-microphone"
                    style={{ fontSize: ".68rem" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <div
        className={`bp-toast${toast.show ? ` show ${toast.type}` : ""}`}
      >
        <i
          className={`fas ${
            toast.type === "success"
              ? "fa-check-circle"
              : "fa-exclamation-circle"
          }`}
        />
        <span>{toast.msg}</span>
      </div>
    </div>
  );
};

export default BusinessProfile;